import { useCallback, useState } from "react";
import useEventListener from "./useEventListener";
import useIsomorphicLayoutEffect from "./useIsomorphicLayoutEffect";

interface Size {
  width: number;
  height: number;
  offsetTop: number;
  scrollTop: number;
  clientTop: number;
}

function useElementSize<T extends HTMLElement = HTMLDivElement>(): [
  (node: T | null) => void,
  Size
] {
  // Mutable values like 'ref.current' aren't valid dependencies
  // because mutating them doesn't re-render the component.
  // Instead, we use a state as a ref to be reactive.
  const [ref, setRef] = useState<T | null>(null);
  const [size, setSize] = useState<Size>({
    width: 0,
    height: 0,
    offsetTop: 0,
    scrollTop: 0,
    clientTop: 0,
  });

  // Prevent too many rendering usign useCallback
  const handleSize = useCallback(() => {
    setSize({
      width: ref?.offsetWidth || 0,
      height: ref?.offsetHeight || 0,
      offsetTop: ref?.offsetTop || 0,
      scrollTop: ref?.scrollTop || 0,
      clientTop: ref?.clientTop || 0,
    });
  }, [ref?.offsetHeight, ref?.offsetWidth, ref?.offsetTop, ref?.clientTop, ref?.scrollTop]);

  useEventListener("resize", handleSize);

  useIsomorphicLayoutEffect(() => {
    handleSize();
  }, [ref?.offsetHeight, ref?.offsetWidth, ref?.offsetTop, ref?.clientTop, ref?.scrollTop]);

  return [setRef, size];
}

export default useElementSize;


